/**
 * Helper functions for organization data processing
 */

// Get plan metadata reducer based on type
export const getPlanMetadataTypeReducer = (type) => {
    switch (type) {
        case 'number':
            return (acc, curr) => {
                const accVal = parseInt(acc)
                const currVal = parseInt(curr)
                // If both are invalid, return undefined
                if (isNaN(accVal) && isNaN(currVal)) return undefined
                // If only one is valid, return the valid one
                if (isNaN(accVal)) return currVal
                if (isNaN(currVal)) return accVal
                // Both valid, return maximum
                return Math.max(accVal, currVal)
            }
        case 'number_min':
            return (acc, curr) => {
                const accVal = parseFloat(acc)
                const currVal = parseFloat(curr)
                // If both are invalid, return undefined
                if (isNaN(accVal) && isNaN(currVal)) return undefined
                // If only one is valid, return the valid one
                if (isNaN(accVal)) return currVal
                if (isNaN(currVal)) return accVal
                // Both valid, return minimum
                return Math.min(accVal, currVal)
            }
        case 'string':
            // For string type, use first non-empty value
            return (acc, curr) => acc || curr
        default:
            // Boolean type - convert to boolean
            return (acc, curr) => acc || curr === 'true'
    }
}

// Plan metadata types mapping
export const PLAN_METADATA_TYPES = {
    '3d': 'boolean',
    customer_pricing: 'boolean',
    customer_supplied: 'boolean',
    fixed_price_parts: 'boolean',
    material_consumption: 'boolean',
    materials_revamp: 'boolean',
    max_users: 'number',
    miscellaneous_items: 'boolean',
    part_library: 'boolean',
    payments_fee_internal: 'number_min',
    payments_fee: 'number_min',
    pdf: 'boolean',
    plan: 'string',
    quickbooks: 'boolean',
    shipping: 'boolean',
    tube: 'boolean',
    webstore: 'boolean',
    xero: 'boolean',
    zapier: 'boolean',
}

/**
 * Extracts metadata from subscription items supporting both Stripe pricing structures.
 *
 * OLD STRUCTURE (price-level metadata):
 *   - subscriptionItem.price.metadata contains all feature flags
 *   - subscriptionItem.price.metadata.max_users = '5'
 *
 * NEW STRUCTURE (product-level metadata):
 *   - subscriptionItem.product.metadata contains all feature flags
 *   - No max_users in metadata, use subscriptionItem.quantity instead
 *
 * @param {Array} subscriptions - Array of subscription objects
 * @returns {Object} Metadata map keyed by priceId or productId
 */
export const extractSubscriptionMetadata = (subscriptions) => {
    if (!subscriptions) return {}

    return subscriptions
        .flatMap((subscription) => subscription.subscriptionItems || [])
        .reduce((metadataMap, subscriptionItem) => {
            // Priority 1: Try price metadata (OLD structure - backward compatibility)
            const priceMetadata = subscriptionItem.price?.metadata
            const hasPriceMetadata = priceMetadata && Object.keys(priceMetadata).length > 1 // More than just $type

            if (hasPriceMetadata) {
                const { $type, ...metadataDto } = priceMetadata
                // Only add if there's actual metadata beyond $type
                if (Object.keys(metadataDto).length > 0) {
                    metadataMap[subscriptionItem.priceId] = metadataDto
                }
            }
            // Priority 2: Try product metadata (NEW structure)
            else if (subscriptionItem.product?.metadata) {
                const { $type, ...metadataDto } = subscriptionItem.product.metadata
                // Only add if there's actual metadata beyond $type
                if (Object.keys(metadataDto).length > 0) {
                    metadataMap[subscriptionItem.product.productId] = metadataDto
                }
            }

            return metadataMap
        }, {})
}

/**
 * Calculates maximum allowed users from subscription data.
 *
 * BUSINESS RULES:
 * 1. Start with max_users from metadata if present (largest value across all subscriptions)
 * 2. Calculate largest quantity across all subscription items
 * 3. Return the MAXIMUM of both values (max_users from metadata AND quantities)
 * 4. Enforce minimum of 1 user (every organization needs at least one user)
 *
 * HYBRID SUBSCRIPTION LOGIC:
 * This dual-logic supports migration from price-level to product-level metadata
 * without requiring existing subscriptions to be migrated. When organizations have
 * both old (price-level with max_users) and new (product-level with quantity)
 * subscriptions, they benefit from the most generous user limit.
 *
 * EXAMPLE:
 * - Subscription A (old): max_users = '5' in price.metadata
 * - Subscription B (new): quantity = 20 in subscriptionItem
 * - Result: max(5, 20) = 20 users (customer gets the higher limit)
 *
 * @param {Array} subscriptions - Array of subscription objects
 * @param {Object} resolvedMetadata - Merged metadata from all subscriptions (max_users already resolved to largest value)
 * @returns {number} Maximum allowed users (minimum 1)
 */
export const calculateMaxUsers = (subscriptions, resolvedMetadata) => {
    let maxUsers = 1

    // Extract max_users from metadata if present (already contains the largest value across all subscriptions)
    if (resolvedMetadata?.max_users !== undefined) {
        maxUsers = Math.max(1, parseInt(resolvedMetadata.max_users) || 1)
    }

    // If we have subscriptions, also consider quantities
    if (subscriptions && subscriptions.length > 0) {
        // Extract all quantities from subscription items
        const quantities = subscriptions
            .flatMap((subscription) => subscription.subscriptionItems || [])
            .map((subscriptionItem) => parseInt(subscriptionItem.quantity) || 1)

        // Return the maximum of max_users (from metadata) and all quantities
        // This ensures customers get the most generous limit when combining subscription types
        maxUsers = Math.max(maxUsers, ...quantities)
    }

    return maxUsers
}

/**
 * Resolve metadata from multiple plans with support for both old and new structures
 *
 * Merges metadata from all subscriptions using type-specific reducers (boolean OR, number MAX).
 *
 * @param {Object} metadata - The metadata keyed by priceId/productId (with internal flags)
 * @returns {Object} Resolved metadata
 */
export const resolveMetadata = (metadata) => {
    if (!metadata || Object.keys(metadata).length === 0) {
        return {}
    }

    // Merge all metadata using existing type reducers, filtering internal flags
    const resolved = Object.values(metadata).reduce((acc, currPlanMetadata) => {
        return Object.keys(currPlanMetadata).reduce((innerAcc, metadataKey) => {
            innerAcc[metadataKey] = getPlanMetadataTypeReducer(PLAN_METADATA_TYPES[metadataKey])(
                innerAcc[metadataKey],
                currPlanMetadata[metadataKey]
            )
            return innerAcc
        }, acc)
    }, {})

    return resolved
}

/**
 * Determine paid features based on subscription data
 * @param {Object} resolvedMetadata - The resolved metadata
 * @param {boolean} isOnTrialValue - Whether the organization is on trial
 * @param {boolean} hasActiveSubscription - Whether the organization has an active subscription
 * @returns {Object} Paid features
 */
export const determinePaidFeatures = (resolvedMetadata, isOnTrialValue, hasActiveSubscription) => {
    return {
        isFreePlan: resolvedMetadata?.plan?.toString().toLowerCase() === 'free',
        hasMaterialConsumptionModeOptions:
            isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.material_consumption),
        hasCustomerPricing: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.customer_pricing),
        hasAdvancedMaterials: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.materials_revamp),
        hasCustomerSuppliedMaterial: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.customer_supplied),
        hasUnfoldTube: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.tube),
        hasPdfUpload: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.pdf),
        hasPartLibrary: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.part_library),
        hasWebStore: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.webstore),
        has3d: isOnTrialValue || (hasActiveSubscription && resolvedMetadata ? resolvedMetadata['3d'] : false),
        hasMiscellaneousItems: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.miscellaneous_items),
        hasFixedPriceParts: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.fixed_price_parts),
        hasShipping: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.shipping),
        hasZapierIntegration: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.zapier),
        hasQuickBooksIntegration: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.quickbooks),
        hasXeroIntegration: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.xero),
        hasShowViewer3D: hasActiveSubscription && resolvedMetadata?.ShowViewer3D,
        hasShowViewerTube: hasActiveSubscription && resolvedMetadata?.ShowViewerTube,
        paymentsFee: resolvedMetadata?.payments_fee,
        paymentsFeeInternal: resolvedMetadata?.payments_fee_internal,
    }
}
